import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Grid, GridColumn, GridDataStateChangeEvent, GridCellProps } from '@progress/kendo-react-grid';
import { process, State } from '@progress/kendo-data-query';

interface User {
  id: string;
  email: string;
  userName: string;
  emailConfirmed: boolean;
  roles: string[];
}

export const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('User');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [userTableError, setUserTableError] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 10,
    sort: [],
    filter: undefined,
  });
  const [processedData, setProcessedData] = useState<any>({ data: [], total: 0 });
  
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await fetch('/api/usermanagement/users', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      setError('Error loading users');
    } finally {
      setIsLoadingUsers(false);
    }
  };
  
  useEffect(() => {
    const processed = process(users, dataState);
    setProcessedData(processed);
  }, [users, dataState]);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const loadRoles = async () => {
    try {
      const response = await fetch('/api/usermanagement/roles', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableRoles(data.map((r: any) => r.name));
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, confirmPassword, role }),
      });

      if (response.ok) {
        setSuccessMessage(`User ${email} registered successfully!`);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setRole('User');
        loadUsers(); // Refresh the user list
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/usermanagement/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccessMessage(`User ${userEmail} deleted successfully`);
        setError('');
        loadUsers();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Error deleting user');
    }
  };

  const startEditingRoles = (user: User) => {
    setEditingUserId(user.id);
    setSelectedRoles([...user.roles]);
  };

  const cancelEditingRoles = () => {
    setEditingUserId(null);
    setSelectedRoles([]);
  };

  const handleRoleToggle = (roleToToggle: string) => {
    if (selectedRoles.includes(roleToToggle)) {
      setSelectedRoles(selectedRoles.filter(r => r !== roleToToggle));
    } else {
      setSelectedRoles([...selectedRoles, roleToToggle]);
    }
  };

  const handleSaveRoles = async (userId: string, userEmail: string) => {
    if (selectedRoles.length === 0) {
      setError('User must have at least one role');
      return;
    }

    try {
      const response = await fetch(`/api/usermanagement/users/${userId}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ roles: selectedRoles }),
      });

      if (response.ok) {
        setSuccessMessage(`Roles updated successfully for ${userEmail}`);
        setError('');
        setEditingUserId(null);
        loadUsers();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update roles');
      }
    } catch (err) {
      setError('Error updating roles');
    }
  };

  const EmailConfirmedCell = (props: GridCellProps) => (
    <td>
      {props.dataItem.emailConfirmed ? (
        <span className="badge bg-success">Yes</span>
      ) : (
        <span className="badge bg-warning">No</span>
      )}
    </td>
  );

  const RolesCell = (props: GridCellProps) => (
    <td>
      {editingUserId === props.dataItem.id ? (
        <div className="role-checkboxes">
          {availableRoles.map((r) => (
            <div key={r} className="form-check form-check-inline">
              <input
                type="checkbox"
                className="form-check-input"
                id={`role-${props.dataItem.id}-${r}`}
                checked={selectedRoles.includes(r)}
                onChange={() => handleRoleToggle(r)}
              />
              <label
                className="form-check-label"
                htmlFor={`role-${props.dataItem.id}-${r}`}
              >
                {r}
              </label>
            </div>
          ))}
        </div>
      ) : (
        <div>
          {props.dataItem.roles.map((r: string) => (
            <span key={r} className="badge bg-primary me-1">
              {r}
            </span>
          ))}
        </div>
      )}
    </td>
  );

  const ActionsCell = (props: GridCellProps) => (
    <td>
      {editingUserId === props.dataItem.id ? (
        <>
          <button
            className="btn btn-success me-2"
            onClick={() => handleSaveRoles(props.dataItem.id, props.dataItem.email)}
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.25rem 0.5rem',
              lineHeight: '1.5'
            }}
          >
            Save
          </button>
          <button
            className="btn btn-secondary"
            onClick={cancelEditingRoles}
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.25rem 0.5rem',
              lineHeight: '1.5'
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          <button
            className="btn btn-outline-primary me-2"
            onClick={() => startEditingRoles(props.dataItem)}
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.25rem 0.5rem',
              lineHeight: '1.5'
            }}
          >
            Edit Roles
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => handleDeleteUser(props.dataItem.id, props.dataItem.email)}
            disabled={currentUser?.email === props.dataItem.email}
            title={
              currentUser?.email === props.dataItem.email
                ? 'Cannot delete your own account'
                : 'Delete user'
            }
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.25rem 0.5rem',
              lineHeight: '1.5'
            }}
          >
            Delete
          </button>
        </>
      )}
    </td>
  );

  return (
    <div className="container mt-2">
      <div className="row">
        <div className="col-md-12">
          <h4 className="text-center">User Management</h4>
          {error && <div className="alert alert-danger">{error}</div>}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}
          
          {/* Registration Form */}
          <div className="card mb-4">
            <div className="card-header">
              <h5>Register New User</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label htmlFor="password">Password</label>
                      <input
                        type="password"
                        id="password"
                        className="form-control"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        minLength={6}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        minLength={6}
                      />
                    </div>
                  </div>
                  <div className="col-md-1">
                    <div className="form-group mb-3">
                      <label htmlFor="role">Role</label>
                      <select
                        id="role"
                        className="form-control"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="User">User</option>
                        <option value="Admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <div className="form-group mb-3">
                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Registering...' : 'Register'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="card-header">
              <h5>Existing Users</h5>
            </div>
            <div className="card-body">
              {userTableError && (
                <div className="alert alert-warning">
                  {userTableError}
                  <button 
                    className="btn btn-sm btn-outline-primary ms-3" 
                    onClick={loadUsers}
                  >
                    Retry
                  </button>
                </div>
              )}
              {isLoadingUsers ? (
                <div className="text-center p-3">Loading users...</div>
              ) : userTableError ? null : (
                <Grid
                  data={processedData}
                  sortable={true}
                  filterable={true}
                  pageable={true}
                  {...dataState}
                  onDataStateChange={handleDataStateChange}
                  style={{ height: '500px' }}
                >
                  <GridColumn field="email" title="Email" />
                  <GridColumn field="userName" title="Username" />
                  {/* <GridColumn 
                    field="emailConfirmed" 
                    title="Email Confirmed" 
                    width="150px"
                    filterable={false}
                    cells={{ data: EmailConfirmedCell }}
                  /> */}
                  <GridColumn 
                    field="roles" 
                    title="Roles" 
                    width="200px"
                    cells={{ data: RolesCell }}
                  />
                  <GridColumn 
                    title="Actions" 
                    width="200px"
                    filterable={false}
                    sortable={false}
                    cells={{ data: ActionsCell }}
                  />
                </Grid>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
